import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { Button } from "@/components/ui/button";
import { useOperatorApi } from "@/hooks/useOperatorApi";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function OperatorClaims() {
  const { t } = useTranslation();
  const { operatorFetch } = useOperatorApi();
  const [claims, setClaims] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [selectedClaim, setSelectedClaim] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [operatorNote, setOperatorNote] = useState("");
  const [adjustmentEuro, setAdjustmentEuro] = useState(0);

  useEffect(() => { loadClaims(); }, [filter]);

  async function loadClaims() {
    setLoading(true);
    let query = supabase.from("opportunity_claims")
      .select("*, provider_profiles(company_name), opportunities(opportunity_number, price)")
      .order("created_at", { ascending: false });

    if (filter === "open") query = query.in("status", ["submitted", "SUBMITTED", "UNDER_REVIEW"]);
    if (filter === "decided") query = query.in("status", ["approved", "APPROVED", "rejected", "REJECTED"]);

    const { data } = await query;
    setClaims(data || []);
    setLoading(false);
  }

  async function markUnderReview(claimId: string) {
    await supabase.from("opportunity_claims").update({ status: "UNDER_REVIEW" }).eq("id", claimId);
    toast.success(t("op.claimUnderReview", "Reklamation wird geprüft"));
    loadClaims();
  }

  async function decideClaim(decision: "approve" | "reject") {
    if (!selectedClaim) return;
    if (decision === "reject" && !operatorNote.trim()) {
      toast.error(t("op.claimRejectNoteRequired", "Eine Begründung ist bei Ablehnung erforderlich."));
      return;
    }
    setActionLoading(true);
    try {
      const endpoint = `billing/claims/${selectedClaim.id}/decide`;

      await operatorFetch(endpoint, {
        method: "POST",
        body: {
          decision: decision === "approve" ? "APPROVED" : "REJECTED",
          operator_note: operatorNote || undefined,
          adjustment_cents: decision === "approve" ? -Math.abs(Math.round(adjustmentEuro * 100)) : 0,
        },
      });
      toast.success(decision === "approve" ? t("op.claimApproveSuccess") : t("op.claimRejectSuccess"));
      setSelectedClaim(null);
      setOperatorNote("");
      setAdjustmentEuro(0);
      loadClaims();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  const FILTER_LABELS: Record<string, string> = {
    open: t("op.claimFilterOpen"),
    decided: t("op.claimFilterDecided"),
    all: t("op.filterAll"),
  };

  return (
    <OperatorLayout title={t("op.navClaims")} subtitle={t("op.claimSubtitle")}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex gap-2">
            {["open", "decided", "all"].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={cn("px-3 py-1.5 text-xs border transition-colors",
                  filter === f ? "border-accent bg-accent/5 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                )}>
                {FILTER_LABELS[f]}
              </button>
            ))}
          </div>

          <div className="border border-border bg-card">
            {loading ? <p className="p-8 text-center text-caption">{t("common.loading")}</p> : claims.length === 0 ? (
              <p className="p-8 text-center text-caption">{t("op.claimEmpty")}</p>
            ) : (
              <div className="divide-y divide-border">
                {claims.map((claim) => (
                  <div key={claim.id}
                    className={cn("p-4 cursor-pointer hover:bg-secondary/30 transition-colors",
                      selectedClaim?.id === claim.id && "bg-accent/5")}
                    onClick={() => setSelectedClaim(claim)}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium">{claim.provider_profiles?.company_name || "—"}</p>
                        <p className="text-micro">{claim.reason_code} · {claim.opportunities?.opportunity_number || "—"}</p>
                      </div>
                      <span className={cn("text-xs px-2 py-1 border",
                        claim.status === "APPROVED" || claim.status === "approved" ? "border-green-400/30 bg-green-500/10 text-green-600" :
                        claim.status === "REJECTED" || claim.status === "rejected" ? "border-red-400/30 bg-red-500/10 text-red-600" :
                        claim.status === "UNDER_REVIEW" ? "border-yellow-400/30 bg-yellow-500/10 text-yellow-600" :
                        "border-border bg-muted text-muted-foreground"
                      )}>
                        {claim.status}
                      </span>
                    </div>
                    {claim.reason_text && <p className="text-caption mt-2">{claim.reason_text}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Detail */}
        <div>
          {selectedClaim ? (
            <div className="border border-border bg-card p-6 space-y-4 sticky top-8">
              <h3 className="text-subheadline">{t("op.claimDetail")}</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-micro block mb-1">{t("op.claimReason")}</span>
                  <p className="text-sm">{selectedClaim.reason_code}</p>
                </div>
                <div>
                  <span className="text-micro block mb-1">{t("op.claimComment")}</span>
                  <p className="text-sm">{selectedClaim.reason_text || "—"}</p>
                </div>
                <div>
                  <span className="text-micro block mb-1">{t("op.claimOppPrice")}</span>
                  <p className="text-sm">{selectedClaim.opportunities?.price || "—"} €</p>
                </div>
                <div>
                  <span className="text-micro block mb-1">{t("op.claimSubmittedAt")}</span>
                  <p className="text-sm">{new Date(selectedClaim.created_at).toLocaleString("de-DE")}</p>
                </div>
              </div>

              {(selectedClaim.status === "submitted" || selectedClaim.status === "SUBMITTED" || selectedClaim.status === "UNDER_REVIEW") && (
                <>
                  {(selectedClaim.status === "submitted" || selectedClaim.status === "SUBMITTED") && (
                    <Button variant="outline" size="sm" className="w-full" onClick={() => markUnderReview(selectedClaim.id)}>
                      {t("op.claimMarkReview", "In Prüfung setzen")}
                    </Button>
                  )}
                  <div className="divider-line" />
                  <div>
                    <label className="text-micro block mb-2">
                      {t("op.claimOperatorNote")}
                      <span className="text-destructive ml-1">*</span>
                      <span className="text-muted-foreground ml-1 font-normal">{t("op.claimNoteRequiredHint", "(Pflicht bei Ablehnung)")}</span>
                    </label>
                    <textarea value={operatorNote} onChange={(e) => setOperatorNote(e.target.value)}
                      className="w-full min-h-[80px] p-3 border border-border bg-background text-sm resize-none focus:outline-none focus:border-primary" />
                  </div>
                  <div>
                    <label className="text-micro block mb-2">{t("op.claimCreditEuro", "Gutschrift (€)")}</label>
                    <input type="number" step="0.01" value={adjustmentEuro} onChange={(e) => setAdjustmentEuro(Number(e.target.value))}
                      className="w-full h-10 px-3 border border-border bg-background text-sm focus:outline-none focus:border-primary" />
                  </div>
                  <div className="flex gap-3">
                    <Button variant="commit" size="sm" onClick={() => decideClaim("approve")} disabled={actionLoading}>
                      {t("op.claimApprove")}
                    </Button>
                    <Button variant="quiet" size="sm" onClick={() => decideClaim("reject")} disabled={actionLoading || !operatorNote.trim()}>
                      {t("op.claimReject")}
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-border p-8 text-center">
              <p className="text-caption">{t("op.claimSelectHint")}</p>
            </div>
          )}
        </div>
      </div>
    </OperatorLayout>
  );
}